import {
  useCapTableTransactions,
  useGetAllDirectorsInCompany,
  useStockClasses,
  useSupportedCorporations,
} from '@commenda/api';
import { IncorporationRequestDataType } from '@commenda/types';
import { PencilLight, ProceedIcon } from '@commenda/icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
} from '@commenda/kakashi';
import {
  DirectorBase,
  TEquityInfoKS,
} from '@commenda/types/src/incorporation/types';
import { TSetNotification } from '@commenda/types/src/random';
import { WorkRequestResponseType } from '@commenda/types/src/work-request/types';
import { fetcher } from '@commenda/utils';
import { IncorporationRequestStatus } from '@prisma/client';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { KeyedMutator } from 'swr';
import { IncorporationFormHeading, SubmitWorkRequestModalKS } from '../common';
import CorporateShareholdersSummary from '../common/CorporateShareholdersSummary';
import {
  calculateStakeholdersEquityMap,
  extractCorporateShareholders,
} from '../common/corporateStakeholder/utils';
import CapTable from '../EquityInfo/CapTable';
import { TShareholderVsSharePercentageMap } from '../EquityInfo/types';
import { generateOwnershipSummaryTableRowsForUSEntities } from '../us-corporate-stakeholders/EquityInfo/utils';
import { EquityDefaultValues } from './Common';
import { SummaryCompanyInfo } from './SummaryCompanyInfo';
import UKDirectorSummaryField from './UKDirectorSummaryField';
import UKMemberSummaryField from './UKMemberSummaryField';

const SummarySection = ({
  setActiveStep,
  onProceed,
  data,
  mutate,
  hideActionButtons = false,
  setNotification,
  token,
  corporationId,
  companyId,
  isReviewSummary = false,
}: {
  companyId: number;
  mutate: KeyedMutator<unknown>;
  setActiveStep: (stepIdx: number) => void;
  onProceed: (idx: number) => void;
  data: WorkRequestResponseType & { requestData: IncorporationRequestDataType };
  hideActionButtons?: boolean;
  token: string;
  setNotification: TSetNotification;
  corporationId: number;
  isReviewSummary?: boolean;
}) => {
  const router = useRouter();

  const { id } = router.query as { id: string };

  const { data: companyData, mutate: companyMutate } =
    useGetAllDirectorsInCompany(companyId);

  const [isSubmitting, setSubmitting] = useState(false);

  const { data: corporationMetaData } = useSupportedCorporations();

  const [directors, setDirector] = useState<DirectorBase[]>([]);

  const { data: capTableTransactions } = useCapTableTransactions(corporationId);

  const { data: stockClasses } = useStockClasses(corporationId);

  const [equityInfo, setEquityInfo] = useState<TEquityInfoKS>({
    shareCount: null,
    price: null,
    shareRetained: null,
  });

  const [selectedStakeholders, setSelectedStakeholders] =
    useState<TShareholderVsSharePercentageMap>();

  const [ownershipSummaryTableData, setOwnershipSummaryTableData] = useState<
    {
      id: string;
      name: string;
      ownership: string;
      actualOwnership: string;
      shares: string;
      esopContribution: string;
    }[]
  >([]);

  // Get individual shareholders from cap table transactions
  const individualShareholders = useMemo(() => {
    if (!capTableTransactions || !companyData?.directors) return [];

    // Get director IDs from cap table transactions
    const directorIdsFromCapTable = capTableTransactions
      .map((tx) => {
        const director = tx.stakeholding?.stakeholder?.director;
        if (!director) return null; // skip corporate shareholders
        return director.id;
      })
      .filter(Boolean);

    // Get full director data from companyData
    const shareholders = companyData.directors.filter((director) =>
      directorIdsFromCapTable.includes(director.id)
    );

    return shareholders;
  }, [capTableTransactions, companyData]);

  // we can pick the first stock class here on the corporation level, as when the incorporation process is ongoing
  // we will only have one stock class
  const stockClass = stockClasses?.[0];

  const getOwnershipSummaryTableForGBEntities = () => {
    // TODO: Fix (replace with GB-specific code)
    const ownershipSummaryTableRows =
      generateOwnershipSummaryTableRowsForUSEntities({
        equityInfo,
        selectedStakeholders,
        corporationType: data?.requestData?.corporationType,
        hasESOPs: false,
      });

    setOwnershipSummaryTableData(ownershipSummaryTableRows);
  };

  useEffect(() => {
    getOwnershipSummaryTableForGBEntities();
  }, [equityInfo, selectedStakeholders]);

  useEffect(() => {
    if (!stockClasses) return;
    if (!capTableTransactions) return;

    const { shareRetained, stakeholdersEquityMap, authorizedShares } =
      calculateStakeholdersEquityMap({
        capTableTransactions,
        stockClass,
      });

    setEquityInfo({
      shareCount: authorizedShares || EquityDefaultValues.shareCount,
      price:
        stockClass?.additionalInformation?.pricePerShare?.amount ||
        EquityDefaultValues.price,
      shareRetained,
    });

    if (capTableTransactions?.length > 0)
      setSelectedStakeholders(stakeholdersEquityMap);
  }, [capTableTransactions, stockClasses]);

  useEffect(() => {
    if (companyId) {
      companyMutate();
    }
  }, [companyId]);

  useEffect(() => {
    const peopleOfCorp = companyData?.directors?.filter((dir) =>
      dir.WorkRequests.some((wr) => wr.id === id)
    );

    const dirs = peopleOfCorp?.filter((dir) => {
      return dir.corporationHasDirector?.find(
        (chd) =>
          chd.corporationId === corporationId &&
          chd.directorId === dir.id &&
          chd.additionalDirectorInformation.isDirector
      );
    });

    setDirector(dirs);
  }, [companyData]);

  const currentCorporationDetails =
    corporationMetaData?.corporations?.[
      data?.corporation?.incorporationCountry
    ]?.[data?.corporation?.jurisdiction]?.[data?.corporation?.corporationType];

  const onSubmit = async () => {
    setSubmitting(true);

    const response = await fetcher(
      `workRequest/${id}`,
      {
        method: 'POST',
        body: {
          status: IncorporationRequestStatus.Submitted,
        },
      },
      token
    );

    if (!response.ok) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message:
          'Something went wrong, please try again or contact Team Commenda if you need help.',
      });
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    onProceed(3);
    router.push('/');
  };

  return (
    <div className="flex flex-col gap-8 max-w-[700px]">
      <IncorporationFormHeading
        text="Summary"
        additionalText="Please make sure the following information is correct."
      />
      <SummaryCompanyInfo
        data={data}
        setActiveStep={setActiveStep}
        currentCorporationDetails={currentCorporationDetails}
        hideActionButtons={hideActionButtons}
      />

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h4
            className="body-16-bold text-text-primary
              "
          >
            Directors
          </h4>
          {hideActionButtons || directors?.length === 0 ? null : (
            <PencilLight
              className="cursor-pointer"
              onClick={() => setActiveStep(1)}
            />
          )}
        </div>
        <div className="flex flex-col gap-3 rounded-sm">
          <Accordion
            className="AccordionRoot flex flex-col gap-3"
            type="single"
            collapsible
          >
            {directors?.map((dir) => (
              <AccordionItem
                className="AccordionItem rounded-md bg-bg-disabled-subtle"
                value={dir.id?.toString()}
                key={dir.id}
              >
                <AccordionTrigger className="w-full ">
                  <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                    <span className="text-base ">
                      {dir?.directorFirstName} {dir?.directorLastName}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className=" p-4 pt-0">
                  <UKDirectorSummaryField
                    key={dir.id}
                    director={dir}
                    data={data}
                  />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      {individualShareholders && individualShareholders.length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h4 className="body-16-bold text-text-primary">
              Individual Shareholders
            </h4>
            {hideActionButtons ? null : (
              <PencilLight
                className="cursor-pointer"
                onClick={() => setActiveStep(3)}
              />
            )}
          </div>
          <div className="flex flex-col gap-3 rounded-sm">
            <Accordion
              className="AccordionRoot flex flex-col gap-3"
              type="single"
              collapsible
            >
              {individualShareholders?.map((dir) => (
                <AccordionItem
                  className="AccordionItem secondary rounded-md bg-bg-disabled-subtle"
                  value={dir.id?.toString()}
                  key={dir.id}
                >
                  <AccordionTrigger className="w-full ">
                    <div className="py-3 px-6 flex justify-between items-center w-full text-text-placeholder">
                      <span className="text-base ">
                        {dir?.directorFirstName} {dir?.directorLastName}
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className=" p-4 pt-0">
                    <UKMemberSummaryField
                      key={dir.id}
                      director={dir}
                      data={data}
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      )}

      {extractCorporateShareholders(capTableTransactions).length > 0 && (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h4 className="body-16-bold text-text-primary">
              Corporate Shareholders
            </h4>
            {hideActionButtons ? null : (
              <PencilLight
                className="cursor-pointer"
                onClick={() => setActiveStep(3)}
              />
            )}
          </div>
          <CorporateShareholdersSummary
            corporateShareholders={extractCorporateShareholders(
              capTableTransactions
            )}
            directorsData={companyData?.directors}
          />
        </div>
      )}

      <div className="flex flex-col gap-3">
        <h4 className="body-16-bold text-text-primary">Shareholders Equity</h4>

        {/* Equity Information Display */}
        {ownershipSummaryTableData && ownershipSummaryTableData.length > 0 ? (
          <>
            <div className="bg-bg-disabled-subtle rounded-md p-4 mb-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-text-placeholder">Total Shares:</span>
                  <span className="ml-2 text-text-primary font-medium">
                    {equityInfo.shareCount?.toLocaleString() || '-'}
                  </span>
                </div>
                <div>
                  <span className="text-text-placeholder">
                    Price Per Share:
                  </span>
                  <span className="ml-2 text-text-primary font-medium">
                    {equityInfo.price ? `£${equityInfo.price}` : '-'}
                  </span>
                </div>
              </div>
            </div>

            <CapTable data={ownershipSummaryTableData} />
          </>
        ) : (
          <p className="text-text-secondary mt-4">
            No ownership data available.
          </p>
        )}
      </div>

      {data.status !== IncorporationRequestStatus.Submitted ? (
        <div className="flex gap-4">
          <Button variant="secondary" onClick={() => setActiveStep(3)}>
            <ProceedIcon className="rotate-180 mr-2 h-4 w-4" />
            <span>Back</span>
          </Button>
          <SubmitWorkRequestModalKS
            isSubmitting={isSubmitting}
            description="This action is irreversible. Once submitted, the details will be
                locked for filing. Please review before submitting."
            onSubmit={onSubmit}
          />
        </div>
      ) : (
        <Button className="flex gap-2 mt-8" onClick={() => router.push('/')}>
          <ProceedIcon className="rotate-180" />
          <span>Back to home</span>
        </Button>
      )}
    </div>
  );
};

export default SummarySection;
